import React, { memo, useMemo } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { categoriesSelectors } from 'app-components/flux/categories';
import { scheduledTransactionsSelectors, scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import type { RecurrenceType } from 'app-components/flux/scheduled-transactions/customFrequencyTypes';
import type { FrequencyAliasOptions } from 'app-components/flux/scheduled-transactions/customFrequencyUtils';
import type { ScheduledTransaction } from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors } from 'app-components/flux/transactions';
import type { TransactionProps } from 'app-components/flux/transactions/transactionsTypes';
import { getLogger } from 'app-components/utils/core';

import { Divider } from '@mui/material';
// MUI
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { getModelHistory } from 'containers/Upcoming/selectors';
import { userFriendlyTxnNames } from 'data/scheduledTransactions/scheduledTransactionsUtils';

import { useAppSelector } from 'hooks/redux';
import { DateTime } from 'luxon';
// DATA
import { useDispatch } from 'react-redux';

// COMPONENTS
import ConnectedAvatar from 'components/ConnectedAvatar';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import Dump from 'components/Dump';
import AmountField, { ShowSignEnum, ShowColorEnum } from 'components/QuickenControls/AmountField';
import QTip from 'components/QuickenControls/QTip';

import RecurringMenu from './RecurringMenu';
import selectPrivacyModeEnabled from 'app-components/hooks/privacyMode/selectPrivacyModeEnabled';

const logger = getLogger();

const useStyles = makeStyles()((theme) => ({
  standardRow: {
    position: 'relative',
    width: '100%',
    display: 'flex',
    padding: theme.spacing(2),
    alignItems: 'center',
    marginTop: 4,
    marginBottom: 4,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer',
      borderRadius: 10,
    },

    '& > td': {
      '@media (min-width: 769px)': {
        paddingRight: theme.spacing(2),
      },
    },
  },
  shadedRow: {
    backgroundColor: '#f0f0f0 !important',
    zIndex: 2,
    '&:hover': {
      backgroundColor: '#f0f0f0 !important',
    },
  },
  tableCellPayee: {
    paddingLeft: theme.spacing(2),
    textOverflow: 'ellipsis',
    flex: 2,
    minWidth: 0,
  },
  tableCellAccount: {
    flex: 1,
    paddingRight: 6,
    paddingLeft: 6,
  },
  tableCellCategory: {
    flex: 1,
  },
  tableCellAmount: {
    textAlign: 'right',
    //  paddingRight: '32px !important',
    flex: 1,
    minWidth: 126,
  },
  inputField: {
    textAlign: 'right',
  },
  ellipsedText: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ellipseTextWrap: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  amtText: {
    fontSize: '16px',
    lineHeight: 1.5,
  },
  canceledChip: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.layer1,
    padding: '2px 4px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
    width: 70,
  },
  noMargin: {
    margin: 0,
  },
  fullWidth: {
    width: '100%',
  },
}));

const daysUntilEOY = Math.floor(DateTime.local().endOf('year').diff(DateTime.local(), 'days').days);

type FriendlyTxnType = {
  id?: string;
  clientId?: string;
  accountId?: string;
  payeeName: string;
  amount: number;
  accountName: string;
  frequency: FrequencyAliasOptions;
  nextDate?: string;
  nextDateFormatted: string;
  recurrence?: RecurrenceType;
  endDate?: string;
  endDateFormatted?: string;
  subtype?: string;
  type: string;
  category: string;
};

function RecurringRow({
  model,
  index,
  selected,
  highlight,
}: {
  model: ScheduledTransaction;
  index: number;
  selected?: boolean;
  highlight?: boolean;
}) {
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();

  const slideKey = model.clientId || model.id;

  const categoriesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const incomeCoaIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);

  const privacyModeEnabled = useAppSelector(selectPrivacyModeEnabled);

  const numberOfPostedTxnsThisYear = useAppSelector((state) => getModelHistory(state, model).history).filter(
    (historyTxn) =>
      DateTime.fromISO(historyTxn.postedOn) > DateTime.local().startOf('year') &&
      DateTime.fromISO(historyTxn.postedOn) < DateTime.local().endOf('year'),
  ).size;

  const upcomingTxns = useAppSelector((state) =>
    transactionsSelectors
      .getAllUpcomingTransactions(state, scheduledTransactionsSelectors.getUserVerifiedScheduledTransactions(state))
      ?.get(model?.id || ''),
  );

  const schTxn = userFriendlyTxnNames(
    model,
    categoriesById,
    accountsById,
    upcomingTxns && upcomingTxns.first(),
    privacyModeEnabled,
  ) as FriendlyTxnType;

  const type = scheduledTransactionsUtils.typeFromScheduledTransaction(model);
  const derivedIsIncome = useMemo(() => {
    const txn =
      model.transaction && 'toJS' in model.transaction
        ? (model.transaction.toJS() as TransactionProps)
        : (model.transaction as TransactionProps | null | undefined);
    return scheduledTransactionsUtils.isIncomeSeriesFromTxn(txn, incomeCoaIds);
  }, [incomeCoaIds, model.transaction]);
  const startView = () =>
    dispatch(
      showViewSeriesDialog({
        ...(model.transaction && 'toJS' in model.transaction ? (model.transaction?.toJS() as TransactionProps) : model),
        stModelId: model.id,
      }),
    );

  const txnValue = () => {
    const { recurrence, amount, frequency } = schTxn;
    if (!recurrence) {
      logger.warn('NO RECURRENCE! \nBad state identified!');
      return amount;
    }
    switch (frequency) {
      case 'Every week':
        if (recurrence.byDay) {
          return amount * recurrence.byDay.length;
        }
        return amount;
      case 'Every month':
        if (recurrence.byMonthDay) {
          return amount * recurrence.byMonthDay.length;
        }
        if (recurrence.bySetPos) {
          return amount * recurrence.bySetPos.length;
        }
        return amount;
      case 'Every year':
        if (recurrence.byMonthDay) {
          if (recurrence.byMonth) {
            return amount * recurrence.byMonth.length * recurrence.byMonthDay.length;
          }
          return amount * recurrence.byMonthDay.length;
        }
        if (recurrence.bySetPos) {
          if (recurrence.byMonth) {
            return amount * recurrence.byMonth.length * recurrence.bySetPos.length;
          }
          return amount * recurrence.bySetPos.length;
        }
        return amount;
      default:
        return amount;
    }
  };

  const txnYearlyValue = (txn: FriendlyTxnType, amount: number) => {
    const { recurrence, frequency } = txn;
    const numberOfUpcomingTxnsThisYear =
      upcomingTxns?.filter(
        (upcomingTxn) =>
          DateTime.fromISO(upcomingTxn.postedOn) > DateTime.local() &&
          DateTime.fromISO(upcomingTxn.postedOn) < DateTime.local().endOf('year'),
      ).size || 0;
    const yearlyTxnNumber = numberOfUpcomingTxnsThisYear + numberOfPostedTxnsThisYear;
    if (!recurrence) {
      logger.warn('NO RECURRENCE! \nBad state identified!');
      return amount;
    }
    switch (frequency) {
      case 'Custom':
      case 'Every year':
      case 'One-time payment':
        return amount;
      case 'Every day':
        return recurrence.alias === 'ONE_TIME_PAYMENT'
          ? amount
          : amount * (numberOfPostedTxnsThisYear + daysUntilEOY > 100 ? daysUntilEOY : numberOfUpcomingTxnsThisYear); // upcoming txns for every day does not go until the end of the year
      case 'Every week':
      case 'Every month':
      case 'Twice a month':
      case 'Every quarter':
        return amount * yearlyTxnNumber;
      case 'Multiple fixed dates':
        if (recurrence.byDates?.length) {
          return amount * yearlyTxnNumber;
        }
        return amount;
      default:
        if (frequency.includes('days') && recurrence.interval) {
          return amount * Math.floor(365 / recurrence.interval);
        }
        if (frequency.includes('weeks') && recurrence.interval) {
          return amount * yearlyTxnNumber;
        }
        if (frequency.includes('months') && recurrence.interval) {
          return amount * yearlyTxnNumber;
        }
        if (frequency.includes('years') && recurrence.interval) {
          return amount / recurrence.interval;
        }
        return amount;
    }
  };
  let backgroundColorBackup: string;

  return (
    <>
      <Slide
        key={slideKey}
        direction='left'
        in
        mountOnEnter
        unmountOnExit
        timeout={highlight ? 800 : 0}
        onEnter={(node) => {
          if (highlight) {
            backgroundColorBackup = node.style.backgroundColor;
            node.style.backgroundColor = theme.palette.background.layer1; // eslint-disable-line no-param-reassign
          }
        }}
        onEntered={(node) => {
          if (highlight) {
            node.style.transition = 'background-color 5s'; // eslint-disable-line no-param-reassign
            node.style.backgroundColor = backgroundColorBackup; // eslint-disable-line no-param-reassign
          }
        }}
      >
        <div
          role='presentation'
          id={`scheduled-txn-${index}`}
          className={cx(classes.standardRow, selected ? classes.shadedRow : '')}
          onClick={startView}
        >
          <Dump obj={model} />

          <ConnectedAvatar model={model} size={40} isCurrentCategoryIncome={derivedIsIncome} />

          <div className={cx(classes.tableCellPayee)}>
            <Box display='flex' flexWrap='nowrap'>
              <Box minWidth={0}>
                <QTip title={schTxn.payeeName} wrapOnly>
                  <Typography
                    style={{ maxWidth: 300 }}
                    variant='bodyRegular'
                    id={`scheduled-txn-${index}-name`}
                    className={classes.ellipsedText}
                  >
                    {schTxn.payeeName}
                  </Typography>
                </QTip>
              </Box>
            </Box>
            {model.isCompleted ? (
              <Typography variant='paragraphRegular' className={classes.canceledChip}>
                Canceled
              </Typography>
            ) : (
              <QTip title={schTxn.nextDateFormatted} wrapOnly>
                <div className={classes.ellipseTextWrap}>
                  <Typography
                    variant='paragraphRegular'
                    id={`scheduled-txn-${index}-next-instance-date`}
                    className={classes.ellipsedText}
                  >
                    {schTxn.nextDateFormatted}
                  </Typography>
                </div>
              </QTip>
            )}
          </div>

          <div className={cx(classes.tableCellAccount, classes.ellipseTextWrap)}>
            <QTip title={schTxn.accountName} wrapOnly>
              <Typography
                variant='bodyRegular'
                id={`scheduled-txn-${index}-source-account`}
                className={classes.ellipsedText}
              >
                {schTxn.accountName}
              </Typography>
            </QTip>
            <Typography
              variant='paragraphRegular'
              id={`scheduled-txn-${index}-account-type`}
              className={classes.ellipsedText}
            >
              {schTxn.subtype}
            </Typography>
          </div>

          <div className={cx(classes.tableCellCategory, classes.ellipseTextWrap)}>
            <QTip title={schTxn.category} wrapOnly>
              <Typography variant='bodyRegular' id={`scheduled-txn-${index}-category`} className={classes.ellipsedText}>
                {schTxn.category}
              </Typography>
            </QTip>
            <Typography variant='paragraphRegular' id={`scheduled-txn-${index}-type`} className={classes.ellipsedText}>
              {scheduledTransactionsUtils.STTypeNamesEnum[type] || type}
            </Typography>
          </div>

          <div className={classes.tableCellAmount}>
            <ListItemText
              primary={
                <AmountField
                  showSign={ShowSignEnum.POSITIVE_ONLY}
                  showColor={ShowColorEnum.POSITIVE_ONLY}
                  value={txnValue()}
                  classes={{ inputField: classes.inputField }}
                  className={classes.amtText}
                  id={`scheduled-txn-${index}-amount`}
                />
              }
              secondary={schTxn.recurrence?.alias === 'ONE_TIME_PAYMENT' ? 'One-time payment' : schTxn.frequency}
              secondaryTypographyProps={{
                variant: 'paragraphRegular',
                id: `scheduled-txn-${index}-frequency`,
              }}
              classes={{ root: classes.noMargin }}
            />
          </div>

          <div className={classes.tableCellAmount}>
            {schTxn.frequency !== 'Custom' && (
              <ListItemText
                primary={
                  <AmountField
                    showSign={ShowSignEnum.POSITIVE_ONLY}
                    showColor={ShowColorEnum.POSITIVE_ONLY}
                    value={txnYearlyValue(schTxn, txnValue())}
                    classes={{ inputField: classes.inputField }}
                    className={classes.amtText}
                    id={`scheduled-txn-${index}-amount`}
                  />
                }
                secondary='Yearly'
                secondaryTypographyProps={{
                  variant: 'paragraphRegular',
                  id: `scheduled-txn-${index}-yearly`,
                }}
                classes={{ root: classes.noMargin }}
              />
            )}
          </div>

          <RecurringMenu model={model} index={index} />
        </div>
      </Slide>
      <Divider className={classes.fullWidth} />
    </>
  );
}

export default memo(RecurringRow);
